<template>
  <master-layout :show-footer="false">
    <div class="min-h-full bg-[#F6F5F1] px-5 py-6 md:px-8">
      <div class="mx-auto flex max-w-[520px] flex-col gap-5">
        <div>
          <p class="text-sm font-semibold uppercase tracking-[0.08em] text-[#6F6A60]">HOGC SSO</p>
          <h1 class="mt-2 text-[1.875rem] font-black leading-9 text-[#25221D]">Connected apps</h1>
          <p class="mt-2 text-base leading-6 text-[#5A554B]">
            Apps you have signed in to with your HOGC One account.
          </p>
        </div>

        <div class="rounded-lg border border-[#E0DDD5] bg-white p-5 shadow-sm">
          <div v-if="loading" class="py-10 text-center text-[#5A554B]">
            Loading your sessions...
          </div>

          <div v-else-if="loadError" class="flex flex-col gap-4">
            <h2 class="text-xl font-bold text-[#25221D]">Unable to load your sessions</h2>
            <p class="text-base leading-6 text-[#5A554B]">{{ loadError }}</p>
            <button
              type="button"
              class="rounded-full bg-[#3D3A35] px-5 py-3 font-semibold text-white"
              @click="fetchSessions"
            >
              Try Again
            </button>
          </div>

          <div v-else-if="sessions.length === 0" class="py-10 text-center">
            <p class="text-base leading-6 text-[#5A554B]">
              You are not signed in to any connected apps.
            </p>
          </div>

          <div v-else class="flex flex-col">
            <!--
              A sign-out that failed is reported here rather than replacing the list. The
              member still needs to see the session, and still needs to be able to try
              again — swapping in a page-level error takes both away and misdescribes
              what went wrong.
            -->
            <p
              v-if="actionError"
              class="mb-4 rounded-md bg-[#FBF0E4] px-4 py-3 text-sm leading-5 text-[#8A5A22]"
            >
              {{ actionError }}
            </p>

            <div class="flex flex-col divide-y divide-[#E0DDD5]">
              <div
                v-for="session in sessions"
                :key="session.id"
                class="flex flex-col gap-3 py-4 first:pt-0 last:pb-0"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <h2 class="text-lg font-bold text-[#25221D]">{{ session.appName }}</h2>
                    <p v-if="deviceLine(session)" class="mt-1 text-sm text-[#6F6A60]">
                      {{ deviceLine(session) }}
                    </p>
                    <p class="mt-1 text-sm text-[#6F6A60]">
                      Signed in {{ relativeTime(session.signedInAt) }}
                    </p>
                  </div>

                  <!--
                    Shown for an unconfirmed sign-out too, not just a live session. The
                    row is there precisely because the app was never told, and a retry is
                    the only thing that resolves it — leaving the member with an error and
                    no control was the whole defect.
                  -->
                  <button
                    v-if="canEnd(session)"
                    type="button"
                    class="shrink-0 rounded-full border border-[#3D3A35] px-4 py-2 text-sm font-semibold text-[#3D3A35] disabled:opacity-50"
                    :disabled="endingId === session.id"
                    @click="endOne(session)"
                  >
                    {{ endLabel(session) }}
                  </button>
                </div>

                <!--
                The status line is only shown once a sign-out has been asked for. ADR-0014
                is explicit that the member should see "signing out..." rather than being
                told it succeeded: this app can revoke its own record immediately, but
                whether the other app has been told is a separate question, and saying so
                is the point.
              -->
                <p
                  v-if="statusNote(session)"
                  class="text-sm leading-5"
                  :class="statusClass(session)"
                >
                  {{ statusNote(session) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <button
          v-if="hasEndableSession"
          type="button"
          class="rounded-full bg-[#3D3A35] px-5 py-3 font-semibold text-white disabled:opacity-50"
          :disabled="endingAll"
          @click="confirmEndAll"
        >
          {{ endingAll ? 'Signing out...' : 'Sign out of all apps' }}
        </button>

        <p class="text-sm leading-5 text-[#6F6A60]">
          Signing out here does not sign you out of HOGC One on this device.
        </p>
      </div>
    </div>
  </master-layout>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';

/** Statuses that mean "this member is still signed in to that app". */
const ACTIVE = 'active';

/**
 * Revoked here, but the app was never successfully told and nothing is retrying it on its
 * own. Asking again is meaningful, so these rows keep a button.
 */
const UNCONFIRMED = 'sign_out_unconfirmed';

export default {
  name: 'SsoConnectedApps',
  data() {
    return {
      loading: true,
      sessions: [],
      // Split deliberately: a page-level load failure replaces the list, an action
      // failure must not. Sharing one field made a failed sign-out render as "unable to
      // load your sessions" and take the list away with it.
      loadError: '',
      actionError: '',
      endingId: null,
      endingAll: false,
    };
  },
  computed: {
    ...mapState('user', ['token']),
    authHeaders() {
      const token = this.token || localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    hasEndableSession() {
      return this.sessions.some(session => this.canEnd(session));
    },
  },
  mounted() {
    this.fetchSessions();
  },
  methods: {
    async fetchSessions() {
      this.loading = true;
      this.loadError = '';
      this.actionError = '';
      try {
        // Deliberately not cached to localStorage, unlike most lists in this app. A
        // stale session list is worse than none: it would show a member sessions they
        // have already ended, or hide one they need to end.
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v2/sso/sessions`, {
          headers: this.authHeaders,
          timeout: 20000,
        });
        this.sessions = data;
      } catch (error) {
        this.loadError =
          error.response?.data?.message || 'Please check your connection and try again.';
      } finally {
        this.loading = false;
      }
    },

    async endOne(session) {
      if (this.endingId) return;
      this.endingId = session.id;
      this.actionError = '';
      try {
        const { data } = await axios.delete(
          `${process.env.VUE_APP_API_URL}/v2/sso/sessions/${session.id}`,
          { headers: this.authHeaders, timeout: 20000 },
        );
        // Reflect what the server actually reported rather than assuming success. The
        // difference between "signed out" and "we could not tell that app" is the whole
        // reason the endpoint returns a status.
        this.applyStatus(session.id, data.status);
      } catch (error) {
        this.actionError =
          error.response?.data?.message ||
          'That session could not be signed out. Please try again.';
      } finally {
        this.endingId = null;
      }
    },

    async confirmEndAll() {
      const confirmed = window.confirm(
        'Sign out of all connected apps? You will stay signed in to HOGC One on this device.',
      );
      if (!confirmed) return;
      await this.endAll();
    },

    async endAll() {
      this.endingAll = true;
      try {
        await axios.delete(`${process.env.VUE_APP_API_URL}/v2/sso/sessions`, {
          headers: this.authHeaders,
          timeout: 20000,
        });
        // Re-read rather than guessing. Sign-out-everywhere can end with a different
        // outcome per app, and only the server knows which.
        await this.fetchSessions();
      } catch (error) {
        this.actionError =
          error.response?.data?.message ||
          'Those sessions could not be signed out. Please try again.';
      } finally {
        this.endingAll = false;
      }
    },

    applyStatus(id, status) {
      // An acknowledged sign-out leaves the list entirely — there is nothing further to
      // say about it, and the server will not return it again either.
      if (status === 'signed_out') {
        this.sessions = this.sessions.filter(session => session.id !== id);
        return;
      }
      this.sessions = this.sessions.map(session =>
        session.id === id ? { ...session, status } : session,
      );
    },

    canEnd(session) {
      return session.status === ACTIVE || session.status === UNCONFIRMED;
    },

    endLabel(session) {
      if (this.endingId === session.id) return 'Signing out...';
      return session.status === UNCONFIRMED ? 'Try again' : 'Sign out';
    },

    deviceLine(session) {
      return [session.deviceLabel, session.location].filter(Boolean).join(' · ');
    },

    statusNote(session) {
      switch (session.status) {
        case 'signing_out':
          return 'Signing out... this app has not confirmed yet.';
        case 'sign_out_unconfirmed':
          return 'We could not confirm the sign-out with this app. Your access here has been withdrawn — tap Try again to retry telling the app.';
        case 'sign_out_not_supported':
          return 'This app cannot be signed out remotely. Your access here has been withdrawn, and the app will sign you out when its session expires.';
        default:
          return '';
      }
    },

    statusClass(session) {
      return session.status === 'signing_out' ? 'text-[#6F6A60]' : 'text-[#8A5A22]';
    },

    relativeTime(value) {
      const then = new Date(value).getTime();
      if (Number.isNaN(then)) return 'recently';
      const minutes = Math.floor((Date.now() - then) / 60000);
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      const days = Math.floor(hours / 24);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    },
  },
};
</script>
